// Home: is the main number open right now, headline numbers, and a shortcut
// card per department.

import { Phone, ArrowRight } from 'lucide-react';
import { useGuide } from './context';
import { nodeName, flowTitle } from './model';
import { Card, OpenBadge, NextChange, Stat, EmptyNote } from './components';
import { queueAvailability } from './lib/callFlowStatus';

export function StartSection() {
  const { t, flow, model, lang, accent, navigate, presence, presenceState } = useGuide();
  const { weekly, holidayRanges } = flow.schedule;
  return (
    <div className="space-y-5">
      <Card className="p-5 relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: accent }} />
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: accent }}>{flowTitle(flow, lang)}</p>
            <h1 className="text-2xl font-semibold text-gray-900 mt-1">{t('heroTitle')}</h1>
            <p className="text-sm text-gray-600 mt-0.5">{t('heroSubtitle')}</p>
            <p className="text-xs text-gray-500 mt-2 max-w-xl">{t('heroText')}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <OpenBadge weekly={weekly} holidays={holidayRanges} iana={model.iana} large />
              <NextChange weekly={weekly} holidays={holidayRanges} iana={model.iana} />
            </div>
            {model.iana && <p className="text-[11px] text-gray-400 mt-1">{t('timeZone', { tz: model.iana })}</p>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{t('mainNumber')}</div>
            {flow.mainNumbers.map(n => (
              <div key={n} className="text-lg font-semibold text-gray-900 tabular-nums flex items-center justify-end gap-1.5">
                <Phone className="w-4 h-4 text-gray-400" />{n}
              </div>
            ))}
            <button type="button" onClick={() => navigate({ section: 'callflow' })}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded font-medium print:hidden" style={{ backgroundColor: accent }}>
              {t('startCallflow')}<ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat value={model.stats.menus} label={t('statsMenus')} onClick={() => navigate({ section: 'hours' })} />
        <Stat value={model.stats.departments} label={t('statsDepartments')} onClick={() => navigate({ section: 'departments' })} />
        <Stat value={model.stats.agents} label={t('statsAgents')} onClick={() => navigate({ section: 'people' })} />
        <Stat value={model.stats.groups} label={t('statsGroups')} onClick={() => navigate({ section: 'departments' })} />
      </div>

      <section>
        <h2 className="text-base font-semibold text-gray-900">{t('quickTitle')}</h2>
        <p className="text-xs text-gray-500 mb-3">{t('quickIntro')}</p>
        {model.queues.length === 0 ? <EmptyNote text={t('noRoute')} /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {model.queues.map(q => {
              const avail = queueAvailability(q.agents, presence);
              const paths = model.pathsByTarget.get(q.id) || [];
              return (
                <Card key={q.id} onClick={() => navigate({ section: 'departments', id: q.id })} className="p-3" accent={accent}>
                  <div className="text-sm font-medium text-gray-900 truncate">{nodeName(q, lang)}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {avail.total} {t('agentsCount')}
                    {presenceState === 'ok' && avail.total > 0 && <> · {t('agentsOf', { available: avail.available, total: avail.total })}</>}
                    {paths.length > 0 && <> · {paths.length} {paths.length === 1 ? t('route').toLowerCase() : t('routes').toLowerCase()}</>}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-base font-semibold text-gray-900 mb-2">{t('howTitle')}</h2>
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-1">
          <li>{t('how1')}</li><li>{t('how2')}</li><li>{t('how3')}</li>
        </ol>
      </section>
    </div>
  );
}
